import {
    DossierDetailType,
    DossierStatusEnum,
    EstablishmentStatus,
    InstitutionalAffiliationType,
    OverViewDossierType,
    Session,
} from "../../../../types";
import { Box, Typography } from "@mui/material";
import LinearProgressWithLabel from "../../../UIElements/LinearProgressWithLabel";
import CustomButton from "../../../UIElements/CustomButton";
import OverViewDossier from "./OverViewDossier";
import { getColorByPercent, buildInstructionInfos } from "../utils";
import Dossier from "../Dossier";
import { useContext } from "react";
import { LayoutBarContext } from "../../../../contexts";
import { useLocalStorage } from "../../../../hooks";
import {
    sessionStorageKey,
    settingStorageKey,
    daysBeforeDeadlineKey,
    remainingDaysBeforeDeadlineKey,
    dateFormat,
} from "../../../../consts";
import DropDown from "../../../UIElements/DropDown";
import { buildInstructorDropDownItems } from "../../utils";
import {
    sendedToValidate,
    getInstructorReview,
    getInstructorAssignedOfDossier,
} from "./utils";
import { formatDate } from "../../../../lib";

type OverViewDossierAdminProps = {
    dossierDetail: DossierDetailType;
    instructorList?: any[];
    toBulkAssignedDossier?: Function;
    linearProgressValue: number;
    validatorDecision?: EstablishmentStatus;
    onPaymentClick?: Function;
    isPayed?: boolean;
    disablePaymentBtn?: boolean;
    downloadCertificate?: Function;
    showDownloadCertificateBtn?: boolean;
    institutionalAffiliation?: InstitutionalAffiliationType;
};

export default function OverViewDossierAdmin({
    dossierDetail,
    instructorList,
    toBulkAssignedDossier,
    linearProgressValue,
    validatorDecision,
    isPayed,
    onPaymentClick,
    disablePaymentBtn,
    downloadCertificate,
    showDownloadCertificateBtn,
    institutionalAffiliation,
}: OverViewDossierAdminProps) {
    const { updateContent } = useContext(LayoutBarContext);

    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        [settingStorageKey]: [...settings],
    } = session;

    const daysBeforeDeadline = Number(
        (settings.find((s) => s.key === daysBeforeDeadlineKey) || {}).value
    );

    const remainingNbDaysToInstructThreshold = Number(
        (settings.find((s) => s.key === remainingDaysBeforeDeadlineKey) || {})
            .value
    );

    const onInstructorSelect = async (instructorSelected: any) => {
        if (toBulkAssignedDossier) {
            await toBulkAssignedDossier([dossierDetail.id], instructorSelected);
        }
    };

    const instructorDropDownItems = instructorList
        ? buildInstructorDropDownItems(instructorList)
        : [];

    const isSendingToValidate = sendedToValidate(dossierDetail);

    const instructorAssignedOfDossier =
        getInstructorAssignedOfDossier(dossierDetail);

    // ########## instructSpace : overview dossier infos to instruct
    const buildOverviewDossierInfosAdmin = (
        dossierDetail: DossierDetailType
    ): OverViewDossierType => {
        const instructorReview = getInstructorReview(dossierDetail);

        const { instructionInfos, remainingNbDaysToInstruct } =
            buildInstructionInfos(dossierDetail, daysBeforeDeadline);

        const dossierInIstruction =
            dossierDetail?.dossierStatus?.code &&
            ![
                DossierStatusEnum.FINALISE,
                DossierStatusEnum.ATTENTE_DECISION,
            ].includes(dossierDetail?.dossierStatus?.code as any);

        const showAssignBtn =
            dossierDetail?.dossierStatus.code !== DossierStatusEnum.FINALISE &&
            dossierInIstruction;

        const notAccessToDossierDetail = dossierDetail.submittedAt === null;

        return {
            alertText:
                remainingNbDaysToInstruct > 0 &&
                remainingNbDaysToInstruct <=
                    remainingNbDaysToInstructThreshold &&
                dossierInIstruction
                    ? `Il ne vous reste que ${remainingNbDaysToInstruct} jours pour instruire ce dossier`
                    : "",
            status: dossierDetail.dossierStatus.label,
            statusCode: dossierDetail.dossierStatus.code,
            subTitle: `Dossier de qualification pour ${dossierDetail.referenciel.name}`,
            title: dossierDetail.establishment.companyName,
            dossierId: dossierDetail?.id,
            onPaymentClick,
            isPayed,
            disablePaymentBtn,
            showDownloadCertificateBtn,
            hidePaymentBtn: notAccessToDossierDetail,
            institutionalAffiliation,
            commitmentsCompleted:
                DossierStatusEnum.CREATION ===
                dossierDetail?.dossierStatus?.code
                    ? dossierDetail.commitmentsCompleted
                    : null,
            content: (
                <>
                    {!notAccessToDossierDetail ? (
                        <>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "0.5rem",
                                    alignSelf: "stretch",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        // flexDirection: "column",
                                        alignItems: "flex-start",
                                        gap: "1rem",
                                        alignSelf: "stretch",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: "0.5rem",
                                            alignSelf: "stretch",
                                            flex: "1 0 0",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: "#555A5D",

                                                fontFamily: "Roboto",
                                                fontSize: "0.875rem",
                                                fontStyle: "normal",
                                                fontWeight: 400,
                                                lineHeight: "normal",
                                                alignSelf: "stretch",
                                            }}
                                        >
                                            Date de soumission adhérent
                                        </Typography>
                                        <Typography
                                            variant="h1"
                                            sx={{
                                                color: "#555A5D",

                                                fontFamily: "Roboto",
                                                fontSize: "1rem",
                                                fontStyle: "normal",
                                                fontWeight: 500,
                                                lineHeight: "normal",
                                                alignSelf: "stretch",
                                            }}
                                        >
                                            <b>
                                                {dossierDetail.submittedAt
                                                    ? formatDate(
                                                          dossierDetail.submittedAt,
                                                          dateFormat
                                                      )
                                                    : ""}
                                            </b>
                                        </Typography>
                                    </Box>
                                    {isSendingToValidate ? (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: "0.5rem",
                                                alignSelf: "stretch",
                                                flex: "1 0 0",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: "#555A5D",

                                                    fontFamily: "Roboto",
                                                    fontSize: "0.875rem",
                                                    fontStyle: "normal",
                                                    fontWeight: 400,
                                                    lineHeight: "normal",
                                                    alignSelf: "stretch",
                                                }}
                                            >
                                                Date de soumission instructeur
                                            </Typography>
                                            <Typography
                                                variant="h1"
                                                sx={{
                                                    color: "#555A5D",

                                                    fontFamily: "Roboto",
                                                    fontSize: "1rem",
                                                    fontStyle: "normal",
                                                    fontWeight: 500,
                                                    lineHeight: "normal",
                                                    alignSelf: "stretch",
                                                }}
                                            >
                                                <b>
                                                    {dossierDetail.processedAt
                                                        ? formatDate(
                                                              dossierDetail.processedAt,
                                                              dateFormat
                                                          )
                                                        : ""}
                                                </b>
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </Box>

                                {showAssignBtn ? (
                                    <DropDown
                                        items={instructorDropDownItems}
                                        defaultValue={
                                            instructorAssignedOfDossier ?? ""
                                        }
                                        placeholder="Affecter un instructeur au dossier"
                                        inputlabel={
                                            "Affecter un instructeur au dossier"
                                        }
                                        onChange={async (event) => {
                                            await onInstructorSelect(
                                                event.target.value
                                            );
                                        }}
                                        selectpx={{
                                            border: "2px solid  #E3E6E8",
                                            background: "#FFF",
                                        }}
                                    />
                                ) : null}
                                {dossierInIstruction ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: "0.5rem",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: "#555A5D",

                                                fontFamily: "Roboto",
                                                fontSize: "0.875rem",
                                                fontStyle: "normal",
                                                fontWeight: 400,
                                                lineHeight: "normal",
                                                alignSelf: "stretch",
                                            }}
                                        >
                                            Instruction du dossier
                                        </Typography>

                                        <LinearProgressWithLabel
                                            value={linearProgressValue}
                                            classes={{
                                                rootsx: {
                                                    width: "100%",
                                                },
                                                linearProgressSx: {
                                                    background: "#E3E6E8",
                                                    ".MuiLinearProgress-bar": {
                                                        backgroundColor:
                                                            getColorByPercent(
                                                                linearProgressValue
                                                            ),
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>
                                ) : null}
                            </Box>
                            {!dossierInIstruction ? (
                                <>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            // flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: "1rem",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: "0.5rem",
                                                alignSelf: "stretch",
                                                flex: "1 0 0",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: "#555A5D",

                                                    fontFamily: "Roboto",
                                                    fontSize: "0.875rem",
                                                    fontStyle: "normal",
                                                    fontWeight: 400,
                                                    lineHeight: "normal",
                                                    alignSelf: "stretch",
                                                }}
                                            >
                                                Avis instructeur
                                            </Typography>
                                            <Typography
                                                variant="h1"
                                                sx={{
                                                    color: "#555A5D",

                                                    fontFamily: "Roboto",
                                                    fontSize: "1rem",
                                                    fontStyle: "normal",
                                                    fontWeight: 500,
                                                    lineHeight: "normal",
                                                    alignSelf: "stretch",
                                                }}
                                            >
                                                <b>{instructorReview}</b>
                                            </Typography>
                                        </Box>

                                        {validatorDecision ? (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "flex-start",
                                                    gap: "0.5rem",
                                                    alignSelf: "stretch",
                                                    flex: "1 0 0",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        color: "#555A5D",

                                                        fontFamily: "Roboto",
                                                        fontSize: "0.875rem",
                                                        fontStyle: "normal",
                                                        fontWeight: 400,
                                                        lineHeight: "normal",
                                                        alignSelf: "stretch",
                                                    }}
                                                >
                                                    Décision SYNERPA
                                                </Typography>
                                                <Typography
                                                    variant="h1"
                                                    sx={{
                                                        color: "#555A5D",

                                                        fontFamily: "Roboto",
                                                        fontSize: "1rem",
                                                        fontStyle: "normal",
                                                        fontWeight: 500,
                                                        lineHeight: "normal",
                                                        alignSelf: "stretch",
                                                    }}
                                                >
                                                    <b>
                                                        {
                                                            validatorDecision.label
                                                        }
                                                    </b>
                                                </Typography>
                                            </Box>
                                        ) : null}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            // flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: "1rem",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: "0.5rem",
                                                alignSelf: "stretch",
                                                flex: "1 0 0",
                                            }}
                                        ></Box>
                                    </Box>
                                </>
                            ) : null}

                            {dossierInIstruction ? (
                                <>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            // flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: "1rem",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: "0.5rem",
                                                alignSelf: "stretch",
                                                flex: "1 0 0",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: "#555A5D",

                                                    fontFamily: "Roboto",
                                                    fontSize: "0.875rem",
                                                    fontStyle: "normal",
                                                    fontWeight: 400,
                                                    lineHeight: "normal",
                                                    alignSelf: "stretch",
                                                }}
                                            >
                                                Affecté le
                                            </Typography>
                                            <Typography
                                                variant="h1"
                                                sx={{
                                                    color: "#555A5D",

                                                    fontFamily: "Roboto",
                                                    fontSize: "1rem",
                                                    fontStyle: "normal",
                                                    fontWeight: 500,
                                                    lineHeight: "normal",
                                                    alignSelf: "stretch",
                                                }}
                                            >
                                                <b>
                                                    {
                                                        instructionInfos.assignedDate
                                                    }
                                                </b>
                                            </Typography>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: "0.5rem",
                                                alignSelf: "stretch",
                                                flex: "1 0 0",
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color: "#555A5D",

                                                    fontFamily: "Roboto",
                                                    fontSize: "0.875rem",
                                                    fontStyle: "normal",
                                                    fontWeight: 400,
                                                    lineHeight: "normal",
                                                    alignSelf: "stretch",
                                                }}
                                            >
                                                Instruction à fournir avant le
                                            </Typography>
                                            <Typography
                                                variant="h1"
                                                sx={{
                                                    color: "#555A5D",

                                                    fontFamily: "Roboto",
                                                    fontSize: "1rem",
                                                    fontStyle: "normal",
                                                    fontWeight: 500,
                                                    lineHeight: "normal",
                                                    alignSelf: "stretch",
                                                }}
                                            >
                                                <b>
                                                    {
                                                        instructionInfos.instructionDeadline
                                                    }
                                                </b>
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            // flexDirection: "column",
                                            alignItems: "flex-start",
                                            gap: "1rem",
                                            alignSelf: "stretch",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: "0.5rem",
                                                alignSelf: "stretch",
                                                flex: "1 0 0",
                                            }}
                                        ></Box>
                                    </Box>
                                </>
                            ) : null}
                        </>
                    ) : null}
                </>
            ),
            actions: !notAccessToDossierDetail
                ? {
                      edit: (
                          <CustomButton
                              onClick={() => {
                                  if (dossierDetail.id) {
                                      updateContent(
                                          <Dossier
                                              dossierId={dossierDetail.id}
                                          />
                                      );
                                  }
                              }}
                              label="Consulter le dossier"
                              customsx={{
                                  width: "100%",
                              }}
                          />
                      ),
                      downloadCertificate,
                  }
                : undefined,
        };
    };

    return (
        <OverViewDossier
            overViewDossierInfo={buildOverviewDossierInfosAdmin(dossierDetail)}
        />
    );
}
